import { useEffect, useRef, useState } from 'react'

import {
  IconCircleXmark,
  IconCircleXmarkSolid,
  IconVerticalDivider,
  ModalDialog
} from '@smartbear/swagger-portal-component-library'
import { Trans, useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router'

import { SearchProductsDesktop } from './SearchProducts/desktop'
import { SearchResults } from './SearchResults'
import { SEARCH_URL } from '~/portal/components/SearchModal/constants'
import { buildSearchParams } from '~/portal/components/SearchModal/helpers'
import appDefaults from '~/portal/portal.defaults.json'
import { SearchLoaderData } from '~/portal/routes/services.api.search/_index'
import { LandingProduct } from '~/portal/types/product'
import { SEARCH_TAB_TYPE } from '~/portal/types/search'
import { addSearchParams } from '~/shared/utils/url'

interface ExitBarProps {
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClose: () => void;
}

const ExitBar = ({ isHovered, onMouseEnter, onMouseLeave, onClose }: ExitBarProps) => (
  <div className="sp-search-modal-exit flex items-center justify-end gap-x-2">
    <span className="font-medium text-body3 text-(--sp-color-text-muted)">
      <Trans i18nKey="components.ui.searchModal.cancel" />
    </span>
    <span className="text-(--sp-color-text-subtle)">
      <IconVerticalDivider />
    </span>
    <div
      className="cursor-pointer"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClose}
    >
      {isHovered ? <IconCircleXmarkSolid /> : <IconCircleXmark />}
    </div>
  </div>
)

interface SearchModalDesktopProps {
  isOpen: boolean;
  onClose: () => void;
  accentColor?: string;
  products?: Array<LandingProduct>,
  currentProduct?: LandingProduct,
}

export const SearchModalDesktop = ({ isOpen, onClose, accentColor, products = [], currentProduct }: SearchModalDesktopProps) => {
  const fetcher = useFetcher<SearchLoaderData>()

  const { t } = useTranslation()

  const [searchData, setSearchData] = useState<SearchLoaderData>({
    results: [],
    noOfElements: {},
  })
  const [searchParams, setSearchParams] = useState({})
  const [isHovered, setIsHovered] = useState(false)
  const [inputLength, setInputLength] = useState(0)
  const [tab, setTab] = useState<SEARCH_TAB_TYPE>(SEARCH_TAB_TYPE.ALL)

  const isLoadingRef = useRef(false)

  const { minQueryLength, maxQueryLength } = appDefaults.search
  
  useEffect(() => {
    if (fetcher.data) {
      setSearchData(fetcher.data)
      isLoadingRef.current = false
    }
  }, [fetcher.data])
  
  const handleSearch = (query: string, type: SEARCH_TAB_TYPE, productId?: string) => {
    if (query?.trim().length < minQueryLength || query?.trim().length > maxQueryLength) {
      setSearchData({ results: [], noOfElements: {} })
    } else {
      isLoadingRef.current = true

      const searchParams = buildSearchParams(query, type, productId)
      setSearchParams(searchParams)
      fetcher.load(addSearchParams(SEARCH_URL, searchParams))
    }
  }
  
  const handleClear = () => {
    setSearchData({ results: [], noOfElements: {} })
    setSearchParams({})
    setInputLength(0)
  }

  useEffect(() => {
    handleClear()
  }, [currentProduct?.id])

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current?.focus()
    }
  }, [isOpen, inputRef.current])

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      customWidth={696}
      className="sp-search-modal h-[38.5rem]"
    >
      <div className="flex flex-col w-full h-full">
        <ExitBar
          isHovered={isHovered}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClose={onClose}
        />
        <div className="pt-6 pb-2">
          <SearchProductsDesktop
            inputRef={inputRef}
            initialText={searchParams['q'] || ''}
            handleClear={handleClear}
            tab={tab}
            onSearch={handleSearch}
            accentColor={accentColor}
            isLoading={fetcher.state === 'loading'}
            onInputLengthChange={setInputLength}
            products={products}
            currentProduct={currentProduct}
            allProductsLabel={t('components.ui.searchModal.allProducts')}
          />
        </div>
        <SearchResults
          selectedTab={tab}
          noOfElements={searchData.noOfElements}
          results={searchData.results || []}
          isLoading={isLoadingRef.current}
          inputLength={inputLength}
          minQueryLength={minQueryLength}
          onClose={onClose}
          onTabChange={setTab}
          searchParams={searchParams}
          totalElements={searchData.searchPage?.totalElements || 0}
        />
      </div>
    </ModalDialog>
  )
}